import { useQuery } from "@tanstack/react-query";
import { Flame, Star } from "lucide-react";
import { Link } from "react-router";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useConfigurationContext } from "../../atomic-crm/root/ConfigurationContext";
import { useRelativeDate } from "../../atomic-crm/misc/RelativeDate";
import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import { scopeToWorkspace } from "./useDashboardMetrics";
import { EmptyState } from "../data-display/EmptyState";
import { SectionCard } from "../data-display/SectionCard";
import { StatusBadge } from "../data-display/StatusBadge";

const RelativeCell = ({ date }: { date: string }) => (
  <>{useRelativeDate(date)}</>
);

interface HotContactRow {
  id: number;
  first_name: string | null;
  last_name: string | null;
  status: string | null;
  company_name: string | null;
  avatar: { src?: string } | null;
  last_seen: string | null;
  nb_tasks: number | null;
}

/**
 * Hot Leads, the workspace's hottest contacts by note status, most recently
 * active first. Real data only; predictive scoring lands with the Gravun AI
 * layer, so today the temperature status is the ranking signal.
 */
export const HotLeadsCard = () => {
  const { noteStatuses } = useConfigurationContext();

  const { data: rows = [], isPending } = useQuery({
    queryKey: ["gravun", "dashboard", "hot-leads", getActiveWorkspaceId()],
    staleTime: 60_000,
    queryFn: async (): Promise<HotContactRow[]> => {
      const { data, error } = await scopeToWorkspace(
        getSupabaseClient()
          .from("contacts_summary")
          .select(
            "id, first_name, last_name, status, company_name, avatar, last_seen, nb_tasks",
          ),
      )
        .eq("status", "hot")
        .order("last_seen", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data ?? [];
    },
  });

  const statusLabel = (value: string | null) =>
    noteStatuses.find((status) => status.value === value)?.label ??
    value ??
    ", ";

  return (
    <SectionCard
      icon={Star}
      title="Hot Leads"
      subtitle="Most engaged contacts, most recent first"
      action="View contacts"
      actionTo="/contacts"
      contentClassName="px-0 pb-2"
    >
      {rows.length === 0 && !isPending ? (
        <EmptyState
          icon={Flame}
          title="No hot leads yet"
          description='Set a contact&apos;s status to "Hot" from a note and it will surface here.'
        />
      ) : (
        <>
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-subtle text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <th className="px-5 py-2 font-medium">Lead</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Open tasks</th>
                <th className="px-5 py-2 text-right font-medium">
                  Last activity
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-border-subtle transition-colors last:border-0 hover:bg-surface-muted"
                >
                  <td className="px-5 py-2.5">
                    <Link
                      to={`/contacts/${row.id}/show`}
                      className="flex items-center gap-2.5 no-underline"
                    >
                      <Avatar className="size-7">
                        <AvatarImage src={row.avatar?.src} alt="" />
                        <AvatarFallback className="bg-primary/10 text-[11px] font-semibold text-primary">
                          {(row.first_name?.[0] ?? "") +
                            (row.last_name?.[0] ?? "")}
                        </AvatarFallback>
                      </Avatar>
                      <span className="leading-tight">
                        <span className="block text-[13px] font-medium text-foreground">
                          {row.first_name} {row.last_name}
                        </span>
                        {row.company_name ? (
                          <span className="block text-[11.5px] text-muted-foreground">
                            {row.company_name}
                          </span>
                        ) : null}
                      </span>
                    </Link>
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusBadge
                      label={statusLabel(row.status)}
                      status={row.status ?? undefined}
                    />
                  </td>
                  <td className="px-3 py-2.5 text-[13px] text-foreground">
                    {row.nb_tasks ?? 0}
                  </td>
                  <td className="px-5 py-2.5 text-right text-[12.5px] text-muted-foreground">
                    {row.last_seen ? (
                      <RelativeCell date={row.last_seen} />
                    ) : (
                      "n/a"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center gap-4 px-5 pt-2.5">
            {[
              { label: "Hot", status: "hot" },
              { label: "Warm", status: "warm" },
              { label: "Cold", status: "cold" },
            ].map((entry) => (
              <span
                key={entry.status}
                className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground"
              >
                <StatusBadge
                  label=""
                  status={entry.status}
                  className="h-2 w-2 rounded-full border-0 p-0"
                />
                {entry.label}
              </span>
            ))}
          </div>
        </>
      )}
    </SectionCard>
  );
};
